import React, { useRef, useState, useEffect } from "react";
import Button from "@material-ui/core/Button";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import HelpIcon from "@mui/icons-material/Help";
import Box from "@material-ui/core/Box";
import { getIntegrationFiles, downloadIntegrationFile } from "@Dao/integrationFilesApi";
import { FileType } from "@Constants/types";
import { APP_DOCS_URL, IsStandaloneDocsEnabled } from "@Constants/tokens";

const useStyles = makeStyles(() => ({
  button: {
    padding: "8px",
    minWidth: "auto",
    color: "#54606F",
    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
  },
  dropdown: { margin: "0 60px 0 -250px" },
  container: { display: "flex", width: "600px", height: "400px" },
  leftPanel: {
    flex: "0 0 40%",
    backgroundColor: "#F3F5F7",
    padding: "20px",
    borderRight: "1px solid #E1E6F1",
  },
  rightPanel: {
    flex: "0 0 60%",
    backgroundColor: "#FFFFFF",
    padding: "20px",
    overflowY: "auto",
  },
  title: { fontSize: "18px", fontWeight: "600", color: "#1A1E24", marginBottom: "20px" },
  section: {
    marginBottom: "5px",
    cursor: "pointer",
    padding: "8px 12px",
    color: "#54606F",
    fontSize: "14px",
    borderRadius: "6px",
    transition: "background-color 0.2s",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "rgba(32, 60, 188, 0.05)" },
  },
  sectionSelected: { backgroundColor: "#FFFFFF" },
  sectionTitle: { fontSize: "13px", fontWeight: "600", color: "#1A1E24", marginBottom: "10px", marginTop: "15px" },
  sectionContent: { fontSize: "13px", color: "#54606F", lineHeight: "1.6" },
  link: {
    color: "#203CBB",
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
  contact: { fontSize: "13px", color: "#54606F", marginTop: "10px", fontWeight: "500" },
}));

const SECTIONS = [
  { key: "instructions", label: "Инструкция пользователей", fileType: FileType.UserManualSmartan, fileName: "Инструкция.pdf" },
  { key: "faq", label: "FAQ (Часто задаваемые вопросы)", fileType: FileType.FAQ, fileName: "FAQ.pdf" },
  { key: "support", label: "Поддержка" },
];

export const HelpButton = () => {
  const [open, setOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState("instructions");
  const [files, setFiles] = useState([]);
  const anchorRef = useRef(null);
  const classes = useStyles();
  const docsUrl = IsStandaloneDocsEnabled() ? APP_DOCS_URL() : "";

  useEffect(() => {
    if (open) {
      loadFiles();
    }
  }, [open]);

  const loadFiles = async () => {
    try {
      const integrationFiles = await getIntegrationFiles();
      setFiles(integrationFiles);
    } catch (err) {
      console.error("Failed to load help files:", err);
    }
  };

  const getFileByType = (fileType) => {
    const fileTypeKey = Object.keys(FileType).find(
      key => FileType[key] === fileType
    );
    return files.find(file => file.fileType === fileTypeKey);
  };

  const handleDownloadFile = async (fileId) => {
    try {
      await downloadIntegrationFile(fileId);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const handleSectionClick = (section) => {
    setSelectedSection(section);
  };

  const renderRightPanel = () => {
    const section = SECTIONS.find(s => s.key === selectedSection);
    
    if (section?.fileType) {
      const file = getFileByType(section.fileType);
      return (
        <Box>
          <div className={classes.sectionTitle}>{section.label}</div>
          <div className={classes.sectionContent}>
            {file ? (
              <a href="#" onClick={(e) => { e.preventDefault(); handleDownloadFile(file.id); }} className={classes.link}>
                {section.fileName} (скачать)
              </a>
            ) : (
              <span></span>
            )}
          </div>
        </Box>
      );
    }

    if (selectedSection === "support") {
      return (
        <Box>
          <div className={classes.sectionTitle}>Поддержка</div>
          <div className={classes.sectionContent}>
            <p>Если у вас возникли вопросы по работе с платформой, рекомендуем начать с простых шагов: проверьте корректность заполнения данных и статус заявки, а также воспользуйтесь разделом «Инструкция пользователей» или «FAQ».</p>
            <p>Если вы не нашли решение — обратитесь в поддержку.</p>
            <div className={classes.contact}>
              Контактные данные:<br />
              info@смартан.рф<br />
              8-961-282-93-40
            </div>
          </div>
        </Box>
      );
    }
    return null;
  };

  return (
    <React.Fragment>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Tooltip title="Помощь" placement="bottom">
          <Button
            ref={anchorRef}
            {...(docsUrl
              ? {
                  component: "a",
                  href: docsUrl,
                  target: "_blank",
                  rel: "noopener noreferrer",
                }
              : {
                  "aria-controls": open ? "help-menu-list-grow" : undefined,
                  "aria-haspopup": "true",
                  onClick: handleToggle,
                })}
            className={classes.button}
          >
            <HelpIcon />
          </Button>
        </Tooltip>
      </div>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        className={classes.dropdown}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <Box className={classes.container} onKeyDown={handleListKeyDown}>
                  <Box className={classes.leftPanel}>
                  <div className={classes.title}>Помощь</div>
                  {SECTIONS.map(s => (
                    <div
                      key={s.key}
                      className={`${classes.section} ${selectedSection === s.key ? classes.sectionSelected : ""}`}
                      onClick={() => handleSectionClick(s.key)}
                    >
                      {s.label}
                    </div>
                  ))}
                </Box>
                  <Box className={classes.rightPanel}>
                    {renderRightPanel()}
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
};
